import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Facebook, FileDown, Globe, Instagram, Linkedin, Lock } from 'lucide-react';
import { mediaUrl } from '../../utils/mediaUrl';
import { getRoles } from '../../utils/authUtils';
import { useLanguage } from '../../contexts/LanguageContext';
import { PROGRAM_ANCHOR } from '../EventImageLink';
import RecommendedStudies from '../self/profile/RecommendedStudies';
import * as SelfApi from '../self/selfApi';
import StudyClaimsList from './StudyClaimsList';
import {
  BLUE, Page, Hero, HeroInner, BigHeadline, SubHeadline, TagLine, Grey, MotivationLabel, MotivationText, PhotoWrap, Photo,
  Initials, Name, TitleLine, HistoryHeader, HistoryTitle, HistorySubtitle, RegCard, RegName, RegMeta, RoleTag,
  EventNameLink, Empty,
} from '../self/profile/profileLayout';
import * as PublicProfileApi from './publicProfileApi';

const API = process.env.REACT_APP_API_URL || '';
const EMPTY_GROUPS = { austrian: [], top: [], new: [] };
// Whether the person's own list holds anything at all; without one claim the owner's section would
// only say that nothing was found.
const hasClaims = (claims) => ['suggestions', 'confirmed', 'rejected']
  .some((key) => (claims?.[key] || []).length > 0);

// The band below the hero (owner decision 2026-10-24, from the mockups of option B). The own profile's
// grid rows did not fit a public page: a long bio stretched the first row and left the right column
// empty. The rule here: blocks of unpredictable height never sit side by side with something that has
// to start below both of them.
//   from 1180px  two columns that run to the end: left the talks, then the studies; right everything
//                about the person (photo, name, title line, links, motivation, bio). The person column
//                stays in view while the page scrolls (sticky), when it fits on the screen.
//   860-1179px   one column: person, motivation and bio, talks, studies, each over the full width; the
//                photo sits in the middle under the hero, which keeps the phone's extra bottom space.
//   up to 859px  one column in the own profile's phone order: person, talks, motivation and bio, studies.
const WIDE = '(min-width: 1180px)';
// How far the photo reaches up into the hero (profileLayout.PhotoWrap): the sticky person column keeps
// that much room above itself so the photo is never cut off.
const PHOTO_OVERLAP_PX = 130;
const Band = styled.div`
  position:relative;max-width:78rem;margin:0 auto;padding:2rem 2.5rem;
  display:grid;align-items:start;column-gap:2.75rem;grid-template-columns:minmax(0,1fr) 27rem;
  @media(max-width:1179px){grid-template-columns:minmax(0,1fr);row-gap:1.75rem;}
  @media(max-width:859px){padding:1.5rem;row-gap:1.5rem;}
`;
const MainColumn = styled.div`
  display:flex;flex-direction:column;gap:1.75rem;min-width:0;padding-top:1.25rem;
  @media(max-width:1179px){display:contents;}
`;
const PersonColumn = styled.div`
  display:flex;flex-direction:column;gap:1.75rem;min-width:0;
  ${(p) => (p.$sticky ? `position:sticky;top:calc(${PHOTO_OVERLAP_PX}px + 1rem);` : '')}
  @media(max-width:1179px){display:contents;}
`;
const PersonBlock = styled.div`@media(max-width:1179px){order:1;}`;
const IntroBlock = styled.div`
  @media(max-width:1179px){order:2;& p{max-width:none;}}
  @media(max-width:859px){order:3;}
`;
const TalksBlock = styled.section`
  min-width:0;
  @media(max-width:1179px){order:3;}
  @media(max-width:859px){order:2;}
`;
const StudiesBlock = styled.div`min-width:0;@media(max-width:1179px){order:4;}`;
// The owner's study list above the feed: only this person ever sees it, so it carries the page's
// section headings and nothing more - the list's own headings and buttons say what it does.
const OwnStudiesBlock = styled.section`margin-bottom:1.75rem;`;
// Between 860 and 1179px the photo sits in the middle under the hero, as on a phone: the hero keeps the
// phone's extra bottom space and the photo the phone's overlap, so it never covers the tagline.
const ProfileHeroInner = styled(HeroInner)`@media(min-width:860px) and (max-width:1179px){padding-bottom:7.5rem;}`;
const ProfilePhotoWrap = styled(PhotoWrap)`@media(min-width:860px) and (max-width:1179px){margin-top:-104px;}`;

// The own profile's photo, without the edit button.
const PhotoFrame = styled.div`position:relative;`;
const Links = styled.div`display:flex;gap:.55rem;justify-content:center;margin-top:.6rem;`;
const IconLink = styled.a`
  display:inline-flex;align-items:center;justify-content:center;width:2rem;height:2rem;border-radius:50%;
  background:#fff;color:#111D4E;box-shadow:0 2px 8px rgba(15,23,42,.08);transition:color .15s;
  &:hover,&:focus-visible{color:${BLUE};}
`;
// The bio keeps the band's shape: about eight lines, then "Read more".
const BioText = styled.p`
  font-size:.92rem;color:#334155;margin:1.1rem 0 0;line-height:1.55;max-width:34rem;white-space:pre-line;
  ${(p) => (p.$clamped ? 'display:-webkit-box;-webkit-line-clamp:8;-webkit-box-orient:vertical;overflow:hidden;' : '')}
`;
const MoreButton = styled.button`background:none;border:0;padding:0;margin-top:.35rem;color:#0369a1;font-weight:600;font-size:.85rem;cursor:pointer;&:hover{text-decoration:underline;}`;
const BodyLine = styled.div`color:#334155;font-size:.85rem;margin-top:.3rem;`;
const TalkList = styled.ul`list-style:none;margin:.6rem 0 0;padding:0;display:flex;flex-direction:column;gap:.65rem;`;
const Talk = styled.li`border-top:1px solid #e5edf2;padding-top:.6rem;`;
const TalkTitle = styled.div`font-weight:700;color:#111D4E;font-size:.95rem;line-height:1.35;margin-top:.25rem;`;
const TalkParent = styled.div`color:#5B6B78;font-size:.8rem;margin-top:.1rem;`;
const TalkMeta = styled.div`color:#5B6B78;font-size:.8rem;margin-top:.15rem;`;
const TalkLinks = styled.div`display:flex;flex-wrap:wrap;gap:.9rem;margin-top:.3rem;`;
const SmallLink = styled(Link)`color:#0369a1;font-size:.8rem;font-weight:600;text-decoration:none;&:hover{text-decoration:underline;}`;
const SlidesLink = styled.a`display:inline-flex;align-items:center;gap:.3rem;color:#0369a1;font-size:.8rem;font-weight:600;text-decoration:none;&:hover{text-decoration:underline;}`;
// A signed-out visitor sees that a talk has slides and where to get them (owner request 2026-11-03):
// the files are for signed-in visitors only. This is a frontend gate, the public slides route itself
// still answers without a login (docs/README.md §9.6).
const LockedSlides = styled.span`display:inline-flex;align-items:center;gap:.3rem;color:#5B6B78;font-size:.8rem;font-weight:600;`;
const LoginLink = styled(Link)`color:#0369a1;text-decoration:underline;&:hover{color:#075985;}`;

const initialsOf = (name) => {
  const words = (name || '').split(/\s+/).filter((w) => w && !w.includes('.'));
  return ((words[0]?.[0] || '') + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase() || '·';
};

// The page language's text, else the other one (the program stores many titles in English only).
const pick = (de, en, language) => (language === 'de' ? (de || en) : (en || de));

// Sets the tab title and the description search engines and link previews read (S18: no noindex), and
// puts back what was there when the page is left.
const useDocumentMeta = (title, description) => {
  useEffect(() => {
    if (!title) return undefined;
    const previousTitle = document.title;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (created) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute('content');
    meta.setAttribute('content', description || '');
    return () => {
      document.title = previousTitle;
      if (created) meta.remove();
      else meta.setAttribute('content', previousDescription || '');
    };
  }, [title, description]);
};

const Bio = ({ text, t }) => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);
  return (
    <>
      <BioText ref={ref} $clamped={!expanded}>{text}</BioText>
      {(overflowing || expanded) && (
        <MoreButton type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
          {expanded ? t('publicProfile.bio.less', 'Show less') : t('publicProfile.bio.more', 'Read more')}
        </MoreButton>
      )}
    </>
  );
};

// The events of the profile in the answer's order (newest first), each with its body roles and its
// appearances (program order). The backend already sends per appearance only the published files the
// person presents; a file is still kept once when the person's spot and its talk both appear (parent
// and topic), so a profile never offers the same download twice.
const eventsOf = (profile) => {
  const bySlug = new Map();
  const of = (event) => {
    if (!bySlug.has(event.slug)) bySlug.set(event.slug, { event, bodies: [], appearances: [] });
    return bySlug.get(event.slug);
  };
  const seenSlides = new Set();
  (profile.bodies || []).forEach((b) => of(b.event).bodies.push(b.body));
  (profile.appearances || []).forEach((a) => {
    const slides = (a.slides || []).filter((slide) => !seenSlides.has(slide.url) && seenSlides.add(slide.url));
    of(a.event).appearances.push({ ...a, slides });
  });
  return [...bySlug.values()].sort((a, b) => String(b.event.startsOn || '').localeCompare(String(a.event.startsOn || '')));
};

// Whether the person column may stay in view: only on a wide screen and only while it fits in the window
// (a long bio opened with "Read more" or a low window would otherwise hide its end for good).
const useStickyWhenItFits = (ref) => {
  const [sticky, setSticky] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const check = () => setSticky(window.matchMedia(WIDE).matches
      && el.offsetHeight + PHOTO_OVERLAP_PX + 32 <= window.innerHeight);
    check();
    window.addEventListener('resize', check);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(check) : null;
    observer?.observe(el);
    return () => {
      window.removeEventListener('resize', check);
      observer?.disconnect();
    };
  });
  return sticky;
};

// A person's public profile (/people/:personId, design study-feed §5.5): the own profile's look
// (profileLayout) without anything private or editable. The profile comes from the database
// (GET /api/public/people/{id}); the HEARTPORT EXPERT studies load separately, with the ECG trace
// meanwhile. A merged id moves to the survivor's address; an unknown, hidden or never-public person is
// the one "not found".
const PublicProfilePage = () => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const { personId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const lang = language === 'de' ? 'de' : 'en';
  const [state, setState] = useState({ status: 'loading', profile: null });
  const [studies, setStudies] = useState(null);
  // {claims} once this page has been recognized as the signed-in visitor's own; null for everyone
  // else, including an anonymous visitor, who never causes the me routes to be called.
  const [owner, setOwner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', profile: null });
    PublicProfileApi.fetchProfile(personId, lang)
      .then((profile) => {
        if (cancelled) return;
        if (profile.mergedInto != null && String(profile.mergedInto) !== String(personId)) {
          navigate(`/people/${profile.mergedInto}`, { replace: true });
          return;
        }
        setState({ status: 'ready', profile });
      })
      .catch(() => { if (!cancelled) setState({ status: 'missing', profile: null }); });
    return () => { cancelled = true; };
  }, [personId, lang, navigate]);

  useEffect(() => {
    let cancelled = false;
    setStudies(null);
    PublicProfileApi.fetchStudies(personId, lang)
      .then((groups) => { if (!cancelled) setStudies(groups || EMPTY_GROUPS); })
      .catch(() => { if (!cancelled) setStudies(EMPTY_GROUPS); });
    return () => { cancelled = true; };
  }, [personId, lang]);

  // The page is the visitor's own when the signed-in account's person is the one in the address. Only
  // then does it ask for the person's own study list: those are me routes and need a login, so a
  // public page never calls them for an anonymous visitor - the stored roles say whether to ask at all.
  useEffect(() => {
    let cancelled = false;
    setOwner(null);
    if ((getRoles() || []).length === 0) return undefined;
    SelfApi.fetchMe()
      .then((me) => {
        if (cancelled || String(me?.personId) !== String(personId)) return null;
        return SelfApi.fetchPublicProfile()
          .then((claims) => { if (!cancelled) setOwner({ claims }); });
      })
      .catch(() => { if (!cancelled) setOwner(null); });
    return () => { cancelled = true; };
  }, [personId]);

  // A decision changes the list and the page's own Austrian group; the backend drops its cached
  // studies for this person on the way (MePublicProfileController), so the new answer is the real one.
  const decideOwnStudy = async (pmid, decision) => {
    await SelfApi.decideStudy(pmid, decision);
    const claims = await SelfApi.fetchPublicProfile().catch(() => null);
    if (claims) setOwner({ claims });
    const groups = await PublicProfileApi.fetchStudies(personId, lang).catch(() => null);
    if (groups) setStudies(groups || EMPTY_GROUPS);
  };

  const personColumn = useRef(null);
  const sticky = useStickyWhenItFits(personColumn);

  const profile = state.profile;
  useDocumentMeta(profile ? `${profile.name} – HEARTS` : null, profile?.titleLine);

  const hero = (
    <Hero>
      <ProfileHeroInner>
        {/* The own profile's customer headline, hardcoded there too. */}
        <BigHeadline>YOUR PORT TO THE ART OF MEDICAL SCIENCE</BigHeadline>
        <SubHeadline>Build your best CARE with HEARTPORT.</SubHeadline>
        <TagLine>Join HEARTS conferences. Connect with experts. Keep up with research. Share your medical art. Apply AI for clinical excellence: DEMOCRATIZING SCIENCE.</TagLine>
      </ProfileHeroInner>
    </Hero>
  );

  if (state.status !== 'ready') {
    return (
      <Page>
        <Hero>
          <HeroInner>
            <Empty>
              {state.status === 'loading'
                ? t('publicProfile.loading', 'Loading the profile…')
                : t('publicProfile.notFound', 'This profile does not exist.')}
            </Empty>
            {state.status === 'missing' && <SmallLink to="/">{t('publicProfile.home', 'To the homepage')}</SmallLink>}
          </HeroInner>
        </Hero>
      </Page>
    );
  }

  const links = [
    ['linkedin', Linkedin, 'LinkedIn'], ['website', Globe, t('publicProfile.links.website', 'Website')],
    ['instagram', Instagram, 'Instagram'], ['facebook', Facebook, 'Facebook'],
  ].filter(([key]) => profile.links?.[key]);
  const formatDay = (day) => (day ? new Date(`${day}T12:00:00`).toLocaleDateString(intl.locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');
  const time = (start, end) => [start, end].filter(Boolean).map((v) => v.slice(0, 5)).join('–');
  const events = eventsOf(profile);
  // Signed in as far as this browser knows (the roles the last login stored); an expired session still
  // gets the links, which the public route answers anyway.
  const signedIn = (getRoles() || []).length > 0;
  const loginPath = `/login?redirect=${encodeURIComponent(`/people/${personId}`)}`;
  const loginLink = (chunks) => <LoginLink to={loginPath}>{chunks}</LoginLink>;

  return (
    <Page>
      {hero}
      <Grey>
        <Band>
          <MainColumn>
            <TalksBlock aria-label="HEARTS Conferences">
              <HistoryHeader>
                {/* The own profile's section title, hardcoded there too. */}
                <HistoryTitle>HEARTS Conferences</HistoryTitle>
                <HistorySubtitle>{t('publicProfile.talks.subtitle', 'Talks, chairs and roles at HEARTS')}</HistorySubtitle>
              </HistoryHeader>
              {events.map(({ event, bodies, appearances }) => (
                <RegCard key={event.slug}>
                  <RegName>
                    <EventNameLink to={`/events/${event.slug}`}>{pick(event.nameDe, event.nameEn, lang) || event.slug}</EventNameLink>
                  </RegName>
                  {event.startsOn && <RegMeta>{formatDay(event.startsOn)}</RegMeta>}
                  {bodies.map((body) => (
                    <BodyLine key={body}>{t(`program.public.body.${body}`, body)}</BodyLine>
                  ))}
                  {appearances.length > 0 && (
                    <TalkList>
                      {appearances.map((a) => (
                        <Talk key={`${a.entryId}-${a.role}`}>
                          <RoleTag>{t(`profile.history.role.${a.role}`, a.role)}</RoleTag>
                          <TalkTitle>{pick(a.entryTitleDe, a.entryTitleEn, lang)}</TalkTitle>
                          {pick(a.parentTitleDe, a.parentTitleEn, lang) && (
                            <TalkParent>{pick(a.parentTitleDe, a.parentTitleEn, lang)}</TalkParent>
                          )}
                          <TalkMeta>
                            {[formatDay(a.day), time(a.startTime, a.endTime), pick(a.roomDe, a.roomEn, lang)].filter(Boolean).join(' · ')}
                          </TalkMeta>
                          <TalkLinks>
                            <SmallLink to={`/events/${event.slug}#${PROGRAM_ANCHOR}`}>{t('publicProfile.talks.program', 'Program')}</SmallLink>
                            {!signedIn && a.slides.length > 0 && (
                              <LockedSlides>
                                <Lock size={13} aria-hidden="true" />
                                <span>
                                  {a.slides.length > 1
                                    ? t('publicProfile.talks.slidesLockedNumbered', '{count} slide files · <link>log in</link> to download',
                                      { count: a.slides.length, link: loginLink })
                                    : t('publicProfile.talks.slidesLocked', 'Slides · <link>log in</link> to download', { link: loginLink })}
                                </span>
                              </LockedSlides>
                            )}
                            {signedIn && a.slides.map((slide, index) => (
                              <SlidesLink key={slide.url} href={`${API}${slide.url}`} download>
                                <FileDown size={14} aria-hidden="true" />
                                {a.slides.length > 1
                                  ? t('publicProfile.talks.slidesNumbered', 'Download slides {position}/{total}',
                                    { position: index + 1, total: a.slides.length })
                                  : t('publicProfile.talks.slides', 'Download slides')}
                              </SlidesLink>
                            ))}
                          </TalkLinks>
                        </Talk>
                      ))}
                    </TalkList>
                  )}
                </RegCard>
              ))}
            </TalksBlock>
            <StudiesBlock>
              {owner && hasClaims(owner.claims) && (
                <OwnStudiesBlock aria-label={t('publicProfile.owner.title', 'Your Austrian studies')}>
                  <HistoryHeader>
                    <HistoryTitle>{t('publicProfile.owner.title', 'Your Austrian studies')}</HistoryTitle>
                    <HistorySubtitle>{t('publicProfile.owner.hint', 'Only you see this section.')}</HistorySubtitle>
                  </HistoryHeader>
                  <StudyClaimsList claims={owner.claims} onDecide={decideOwnStudy} />
                </OwnStudiesBlock>
              )}
              <RecommendedStudies t={t} groups={studies} subjectName={profile.shortName || profile.name}
                loadMore={(group, offset) => PublicProfileApi.fetchStudiesPage(personId, group, offset, lang)} />
            </StudiesBlock>
          </MainColumn>

          <PersonColumn ref={personColumn} $sticky={sticky}>
            <PersonBlock>
              <ProfilePhotoWrap>
                <PhotoFrame>
                  {profile.photoUrl
                    ? <Photo src={mediaUrl(profile.photoUrl)} alt="" referrerPolicy="no-referrer" />
                    : <Initials>{initialsOf(profile.shortName || profile.name)}</Initials>}
                </PhotoFrame>
                <Name as="h2">{profile.name}</Name>
                {profile.titleLine && <TitleLine>{profile.titleLine}</TitleLine>}
                {links.length > 0 && (
                  <Links>
                    {links.map(([key, Icon, label]) => (
                      <IconLink key={key} href={profile.links[key]} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}>
                        <Icon size={16} aria-hidden="true" />
                      </IconLink>
                    ))}
                  </Links>
                )}
              </ProfilePhotoWrap>
            </PersonBlock>
            {(profile.motivation || profile.bio) && (
              <IntroBlock>
                {profile.motivation && (
                  <>
                    <MotivationLabel>{t('profile.motivationLabel', 'I am part of HEARTS - DEMOCRATIZING SCIENCE - because')}</MotivationLabel>
                    <MotivationText>{profile.motivation}</MotivationText>
                  </>
                )}
                {profile.bio && <Bio text={profile.bio} t={t} />}
              </IntroBlock>
            )}
          </PersonColumn>
        </Band>
      </Grey>
    </Page>
  );
};

export default PublicProfilePage;
